import React, { useState } from 'react';
import { LocationTreeModal } from './LocationTree';
import './DeleteButton.css';
import './MergeButton.css';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// "Merge into...": pick another place, read a summary of what will happen, then confirm.
// Everything inside this place moves to the chosen one and this place is deleted.
function MergeButton({ sourceId, locations, onMerged, className }) {
  const [picking, setPicking] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const call = async (targetId, dryRun) => {
    const response = await fetch(`/api/locations/${sourceId}/merge-into`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetId, dryRun })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Merge failed (${response.status})`);
    return data;
  };

  const preview = async (targetId) => {
    setPicking(false);
    setError('');
    setBusy(true);
    try {
      setSummary(await call(targetId, true));
    } catch (e) {
      setSummary({ failed: true });
      setError(e.message);
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const done = await call(summary.target.id, false);
      setSummary(null);
      onMerged(done.target.id);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const close = () => {
    setSummary(null);
    setError('');
  };

  return (
    <>
      <button type="button" className={className} onClick={() => setPicking(true)}>
        Merge into…
      </button>

      {picking && (
        <LocationTreeModal
          title="Merge into…"
          locations={locations}
          excludeIds={[sourceId]}
          onSelect={(id) => preview(id)}
          onClose={() => setPicking(false)}
        />
      )}

      {summary && (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-box merge-box">
            {summary.failed ? (
              <>
                <p className="confirm-message">Cannot merge</p>
                <p className="confirm-error">{error}</p>
                <div className="confirm-actions">
                  <button type="button" className="btn btn-secondary" onClick={close}>Close</button>
                </div>
              </>
            ) : (
              <>
                <p className="confirm-message">
                  Merge “{summary.source.name}” into “{summary.target.path}”?
                </p>
                <ul className="merge-list">
                  <li>
                    {plural(summary.itemsMoved, 'item')} and {plural(summary.compartmentsMoved, 'compartment')} move to
                    “{summary.target.name}”.
                  </li>
                  {summary.compartmentsMerged.length > 0 && (
                    <li>
                      Compartments that exist in both are merged:{' '}
                      <strong>{summary.compartmentsMerged.join(', ')}</strong>.
                    </li>
                  )}
                  {summary.itemsIgnored.length > 0 && (
                    <li className="merge-warn">
                      {plural(summary.itemsIgnored.length, 'item')} already in “{summary.target.name}” will be ignored and
                      deleted with “{summary.source.name}”:{' '}
                      <strong>
                        {summary.itemsIgnored
                          .map((i) => (i.where.includes('›') ? `${i.name} (in ${i.where.split(' › ').slice(1).join(' › ')})` : i.name))
                          .join(', ')}
                      </strong>
                      .
                    </li>
                  )}
                  <li>
                    “{summary.source.name}” is then deleted
                    {summary.compartmentsDeleted > 1 ? `, along with ${plural(summary.compartmentsDeleted - 1, 'merged compartment')}` : ''}.
                  </li>
                </ul>
                {error && <p className="confirm-error">{error}</p>}
                <div className="confirm-actions">
                  <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>Cancel</button>
                  <button type="button" className="btn btn-danger" onClick={confirm} disabled={busy}>
                    {busy ? 'Merging...' : 'Merge'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default MergeButton;
